import React from "react";
import { Button } from "../ui/button";
import { Sparkles, Printer, FolderCheck, TrendingUp } from "lucide-react";
import { heroImage, OFFER_ANCHOR, CTA_TEXT } from "../../data/mock";

const Hero = () => {
  return (
    <section className="relative overflow-hidden">
      {/* Decorative background */}
      <div className="absolute inset-0 -z-0 pointer-events-none">
        <div className="absolute -top-24 -left-24 w-72 md:w-96 h-72 md:h-96 rounded-full bg-pink-200/60 blur-3xl" />
        <div className="absolute -bottom-24 -right-24 w-72 md:w-96 h-72 md:h-96 rounded-full bg-rose-200/60 blur-3xl" />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 pt-6 md:pt-12 pb-6 md:pb-10">
        {/* Impact phrase */}
        <div className="mx-auto max-w-3xl text-center mb-4 md:mb-8">
          <div className="inline-flex items-center gap-2 bg-white border border-pink-200 shadow-sm rounded-full px-3 py-1 text-pink-700 text-[11px] md:text-sm font-semibold uppercase tracking-wider">
            <Sparkles className="w-3.5 h-3.5 md:w-4 md:h-4" />
            Edição Especial 2026
          </div>
          <p className="mt-3 md:mt-4 text-base md:text-2xl font-semibold text-pink-800 leading-snug px-2">
            Você nunca mais precisará criar suas artes do zero.
            <br className="hidden md:block" />
            <span className="shimmer-text"> Basta editar, imprimir e vender.</span>
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 md:gap-10 items-center">
          {/* Left: text */}
          <div className="order-2 md:order-1 text-center md:text-left">
            <h1 className="font-serif-display font-black text-3xl sm:text-4xl md:text-6xl leading-[1.05] tracking-tight text-pink-700">
              ACERVO DA <span className="text-rose-600">ARTESÃ</span>
            </h1>
            <p className="mt-3 md:mt-5 text-sm md:text-lg text-pink-900/80 max-w-xl mx-auto md:mx-0 px-2 md:px-0">
              Mais de <span className="line-through decoration-2 decoration-red-500 text-red-500 font-semibold">700</span> <strong className="text-green-600 font-black">1 MIL</strong> <strong className="text-pink-700">artes exclusivas</strong> para transformar seus produtos em experiências que <strong>encantam</strong>, <strong>valorizam</strong> e fazem suas clientes voltarem sempre.
            </p>

            <ul className="mt-4 md:mt-6 grid gap-2 md:gap-2.5 text-left max-w-md mx-auto md:mx-0">
              <li className="flex items-center gap-3 text-pink-900 text-sm md:text-base">
                <span className="w-7 h-7 md:w-8 md:h-8 grid place-content-center rounded-full bg-pink-100 text-pink-600 shrink-0"><Printer className="w-3.5 h-3.5 md:w-4 md:h-4" /></span>
                <span>Pronto para <strong>imprimir</strong> — alta resolução</span>
              </li>
              <li className="flex items-center gap-3 text-pink-900 text-sm md:text-base">
                <span className="w-7 h-7 md:w-8 md:h-8 grid place-content-center rounded-full bg-pink-100 text-pink-600 shrink-0"><FolderCheck className="w-3.5 h-3.5 md:w-4 md:h-4" /></span>
                <span>Arquivos <strong>organizados</strong> por categoria</span>
              </li>
              <li className="flex items-center gap-3 text-pink-900 text-sm md:text-base">
                <span className="w-7 h-7 md:w-8 md:h-8 grid place-content-center rounded-full bg-pink-100 text-pink-600 shrink-0"><TrendingUp className="w-3.5 h-3.5 md:w-4 md:h-4" /></span>
                <span>Mais <strong>valor percebido</strong> e mais vendas</span>
              </li>
            </ul>

            <div className="mt-5 md:mt-8 px-2 md:px-0">
              <a href={OFFER_ANCHOR} className="block sm:inline-block">
                <Button size="lg" className="w-full sm:w-auto animate-pulse-soft bg-gradient-to-b from-pink-500 to-rose-600 hover:from-pink-600 hover:to-rose-700 text-white font-bold text-sm md:text-lg px-6 md:px-8 py-5 md:py-6 rounded-full shadow-lg">
                  👉 {CTA_TEXT}
                </Button>
              </a>
              <p className="mt-2 md:mt-3 text-[11px] md:text-xs text-pink-900/60">Acesso imediato • Compra 100% segura</p>
            </div>
          </div>

          {/* Right: hero image with floating badge */}
          <div className="order-1 md:order-2 flex justify-center">
            <div className="relative animate-floaty">
              <div className="absolute -inset-4 bg-gradient-to-br from-pink-200 via-rose-200 to-amber-100 rounded-[2rem] blur-2xl opacity-70" />

              {/* Floating badge on top */}
              <div className="absolute -top-3 md:-top-4 left-1/2 -translate-x-1/2 z-10">
                <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white font-black text-[11px] sm:text-xs md:text-sm uppercase tracking-widest px-4 md:px-5 py-2 rounded-full shadow-xl border-2 border-white whitespace-nowrap">
                  ✨ 1 MIL ARTES PRONTAS
                </div>
              </div>

              <img
                src={heroImage}
                alt="Acervo da Artesã - 1 mil artes prontas"
                className="relative w-full max-w-[280px] sm:max-w-sm md:max-w-md drop-shadow-2xl"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
